import React from 'react';

import { Box, Button, Collapse, Typography } from '@material-ui/core';
import { FiChevronDown, FiInfo, FiSliders } from 'react-icons/fi';
import clsx from 'clsx';

import ChatAvatar from '@vyce/core/src/components/Chat/ChatAvatar';
import { DetailsIcon } from '@vyce/core/src/components/DetailsIcon';
import { AppTextField } from '@vyce/core/src/components/inputs';
import { PermissionCheckList } from '@vyce/core/src/modules/rolesModule/components/PermissionCheckList';
import { PermissionDiffBanner } from '@vyce/core/src/modules/rolesModule/components/PermissionDiffBanner';
import { RoleChipDropdown } from '@vyce/core/src/modules/rolesModule/components/RoleChipDropdown';
import { ScopeSelector } from '@vyce/core/src/modules/rolesModule/components/ScopeSelector';
import { useRolesStyles } from '@vyce/core/src/modules/rolesModule/styles';
import { toDisplayName } from '@vyce/core/src/modules/rolesModule/utils/permissionState';
import { getModuleIcon } from '@vyce/core/src/modules/rolesModule/utils/staffModules';
import { requiresResourceSelection } from '@vyce/core/src/utils/staffPermissions';
import useInviteStyles from '@vyce/core/src/views/invites/styles';

import { AdminInviteDialogApi } from '../../hooks/useAdminInviteDialog';
import useStyles from '../../styles';

interface Props {
  dialog: AdminInviteDialogApi;
}

export const AdminRoleStep: React.FC<Props> = ({ dialog }) => {
  const classes = useStyles();
  const inviteClasses = useInviteStyles();
  const rolesClasses = useRolesStyles();
  const { pickedUser, draft, modules, catalogue } = dialog;
  const resolveLabel = (systemName: string) =>
    catalogue ? toDisplayName(systemName, catalogue.index) : systemName;

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Box display="flex" alignItems="center" gridGap={12}>
        <ChatAvatar name={dialog.personName} avatarUrl={pickedUser?.avatar} size={40} />
        <Box flex={1} minWidth={0}>
          <Typography className={inviteClasses.sectionTitle} noWrap>
            {dialog.personName}
          </Typography>
          <Typography className={inviteClasses.sectionCaption} noWrap>
            {dialog.isExisting
              ? dialog.staffItem
                ? 'Already an admin — you are editing their current access'
                : 'Gets access straight away'
              : 'New admin — invited by email'}
          </Typography>
        </Box>
        <Button size="small" color="primary" className={inviteClasses.linkButton} onClick={dialog.goBack}>
          Change
        </Button>
      </Box>

      <AppTextField
        fullWidth
        value={dialog.position}
        onChange={event => dialog.setPosition(event.target.value)}
        label="Position"
        InputProps={{
          endAdornment: (
            <Box marginRight={1}>
              <DetailsIcon
                content={<>Position will be displayed on user’s profile.</>}
                popoverClass={classes.popoverClass}
              />
            </Box>
          ),
        }}
      />

      <Box>
        {modules.map(module => {
          const current = draft[module.systemName];
          const Icon = getModuleIcon(module.systemName);
          const role = current?.role || null;
          const scoped = !!role && requiresResourceSelection(role);
          // The scope can't be read here, so offer only roles that need no resource
          const roleOptions = module.inventoryUnavailable
            ? module.roles.filter(item => !requiresResourceSelection(item))
            : module.roles;
          const permissionState = dialog.permissions[module.systemName];
          const canCustomise = dialog.isModuleCustomisable(module);
          const customised = canCustomise && dialog.customisedModules.includes(module.systemName);
          const customising = canCustomise && !!dialog.customising[module.systemName];
          // On an invite the rows are the server's: wait for them before drawing ticks
          const rowsPending = dialog.customisesInvite && dialog.inviteRowsStatus !== 'ready';
          const autoAdded = dialog.autoAddedByModule[module.systemName] || [];

          return (
            <div key={module.systemName} className={rolesClasses.moduleRow}>
              <div className={rolesClasses.moduleRowMain}>
                <span className={rolesClasses.moduleIcon}>
                  <Icon size={18} />
                </span>
                <div className={rolesClasses.moduleLabel}>
                  {module.label}
                  {customised && <div className={rolesClasses.moduleSubtitle}>Custom permissions</div>}
                  {scoped && module.emptyInventory && (
                    <div className={rolesClasses.moduleSubtitle}>
                      No {(module.resourceLabel || 'resources').toLowerCase()} yet — set the scope once the
                      first one is created
                    </div>
                  )}
                </div>
                <RoleChipDropdown
                  moduleSystemName={module.systemName}
                  value={role}
                  unresolvedRoleName={current?.unresolvedRoleName}
                  options={roleOptions}
                  disabled={dialog.saving}
                  onChange={nextRole => dialog.setModuleRole(module.systemName, nextRole)}
                />
              </div>

              {scoped && module.resources.length > 0 && (
                <Box marginTop={1.5}>
                  <ScopeSelector
                    label={module.resourceLabel || 'Resources'}
                    resources={module.resources}
                    selectedIds={current.resourceIds}
                    disabled={dialog.saving}
                    onToggle={(id, checked) => dialog.toggleModuleResource(module.systemName, id, checked)}
                    onSelectAll={checked => dialog.toggleAllModuleResources(module.systemName, checked)}
                  />
                </Box>
              )}

              {canCustomise && catalogue && (
                <>
                  <Button
                    size="small"
                    color="primary"
                    className={clsx(inviteClasses.linkButton, classes.customiseButton)}
                    disabled={dialog.saving}
                    startIcon={<FiSliders size={13} />}
                    endIcon={
                      <span className={clsx(rolesClasses.chevron, customising && rolesClasses.chevronOpen)}>
                        <FiChevronDown size={13} />
                      </span>
                    }
                    onClick={() => dialog.toggleCustomising(module.systemName)}
                  >
                    Customise permissions
                  </Button>
                  <Collapse in={customising} unmountOnExit>
                    <div className={rolesClasses.moduleDetail}>
                      <div className={rolesClasses.sectionLabel}>
                        {role ? `${role.role_name} — permissions` : 'Given by their other roles'}
                      </div>
                      {rowsPending ? (
                        dialog.inviteRowsStatus === 'error' ? (
                          <Box display="flex" alignItems="center" gridGap={8}>
                            <Typography className={inviteClasses.hint}>
                              Couldn't load the permissions for these roles.
                            </Typography>
                            <Button
                              size="small"
                              color="primary"
                              className={inviteClasses.linkButton}
                              onClick={dialog.retryInviteRows}
                            >
                              Try again
                            </Button>
                          </Box>
                        ) : (
                          <Typography className={inviteClasses.hint}>Loading permissions…</Typography>
                        )
                      ) : (
                        <>
                          <PermissionCheckList
                            groups={module.groups}
                            effective={dialog.effectiveByModule[module.systemName] || new Set<string>()}
                            explicit={new Set(permissionState.explicit)}
                            defaults={permissionState.roleDefaults}
                            added={permissionState.added}
                            removed={permissionState.removed}
                            conflictNames={
                              new Set(
                                permissionState.conflicts.flatMap(pair => [pair.permission, pair.requires])
                              )
                            }
                            disabled={dialog.saving}
                            catalogue={catalogue}
                            onToggle={(systemName, checked) =>
                              dialog.togglePermission(module.systemName, systemName, checked)
                            }
                          />
                          {(customised || !!permissionState.conflicts.length) && (
                            <PermissionDiffBanner
                              roleName={role?.role_name || 'this role'}
                              clearResourceOverrides
                              hasExceptions={false}
                              conflicts={permissionState.conflicts}
                              disabled={dialog.saving}
                              resolveLabel={resolveLabel}
                              onClearResourceOverridesChange={() => {}}
                              onReset={() => dialog.resetModulePermissions(module.systemName)}
                            />
                          )}
                          {!!autoAdded.length && (
                            <Typography className={inviteClasses.hint}>
                              Also added, because a permission you ticked needs{' '}
                              {autoAdded.length === 1 ? 'it' : 'them'}:{' '}
                              {autoAdded.map(resolveLabel).join(', ')}.
                            </Typography>
                          )}
                        </>
                      )}
                    </div>
                  </Collapse>
                </>
              )}
            </div>
          );
        })}
      </Box>

      {!!dialog.autoAddedRows.length && (
        <div className={clsx(rolesClasses.banner, rolesClasses.bannerInfo)}>
          <FiInfo size={15} />
          <div className={rolesClasses.bannerText}>
            The invite also gives{' '}
            {dialog.autoAddedRows
              .map(row => `${row.permissions.map(resolveLabel).join(', ')} (${row.label})`)
              .join('; ')}{' '}
            — the permissions you ticked need {dialog.autoAddedRows.length === 1 ? 'it' : 'them'}.
          </div>
        </div>
      )}

      {dialog.checkingInvite && (
        <Typography className={inviteClasses.hint}>Checking the custom permissions…</Typography>
      )}

      {dialog.hasOwnerRole ? (
        <Typography className={inviteClasses.hint}>
          The Owner role has full access to everything, so there's nothing to customise.
        </Typography>
      ) : (
        !dialog.canCustomise && (
          <Typography className={inviteClasses.hint}>
            Need finer control? Customise their permissions from the admins list once they accept the invite.
          </Typography>
        )
      )}

      {!!dialog.inviteError && (
        <Typography variant="body2" className={inviteClasses.errorText}>
          {dialog.inviteError}
        </Typography>
      )}

      {dialog.invalidReason && (
        <Typography variant="body2" className={inviteClasses.errorText}>
          {dialog.invalidReason}
        </Typography>
      )}
    </Box>
  );
};
